import React, { useMemo } from 'react'
import { ChartMultiPoint, ChartPoint, MultiBar, SeriesMetaData } from './ChartContent'
import { SsvgChartContent } from './styles'

interface ChartBarSeriesProps {
	points: ChartPoint[] | ChartMultiPoint[]
	meta: SeriesMetaData | undefined
	xRange: { beg: number | undefined; end: number | undefined }
	yRange: { beg: number | undefined; end: number | undefined }
	color: string[]
	barSpace: number
	barIndex?: number
	indexesCount?: number
	interval?: number
	isLog?: boolean
	opacities?: number[]
}

interface Bar {
	x: number
	ys: number[]
	width: number
}
const svgDim = 1000
const ChartBarSeries: React.FC<ChartBarSeriesProps> = ({ points, meta, xRange, yRange, color, barSpace, barIndex, indexesCount, interval, isLog, opacities }) => {
	const pointsFormatted = useMemo<ChartMultiPoint[] | undefined>(() => {
		if (points === undefined) {
			return undefined
		}
		if (points.length === 0 || (points as ChartPoint[])[0].y !== undefined) {
			return (points as ChartPoint[]).map((pt) => {
				return { x: pt.x, ys: [pt.y] }
			})
		}
		return points as ChartMultiPoint[]
	}, [points])

	const viewBox = useMemo<[number, number, number, number]>(() => {
		if (yRange?.beg === undefined || yRange?.end === undefined) {
			return [0, 0, svgDim, svgDim]
		}
		if (xRange?.beg === undefined || xRange?.end === undefined) {
			return [0, 0, svgDim, svgDim]
		}
		if (!meta) {
			return [0, 0, svgDim, svgDim]
		}
		const viewBox: [number, number, number, number] = [0, 0, svgDim, svgDim]
		const min = Math.min(0, meta.min)
		const max = Math.max(0, meta.max)
		const xRangeLength = xRange.end - xRange.beg
		const yRangeLength = yRange.end - yRange.beg
		const xMetaRange = meta.end - meta.beg
		const yMetaRange = max - min
		viewBox[0] = ((xRange.beg - meta.beg) / xMetaRange) * svgDim
		viewBox[1] = ((max - yRange.end) / yMetaRange) * svgDim
		viewBox[2] = (xRangeLength / xMetaRange) * svgDim
		viewBox[3] = (yRangeLength / yMetaRange) * svgDim
		return viewBox
	}, [xRange, yRange, meta])

	const pathStates = useMemo<{ points: string[]; color: string }[] | undefined>(() => {
		if (!meta || pointsFormatted === undefined) return undefined
		if (xRange?.beg === undefined || xRange?.end === undefined) return undefined
		if (yRange?.beg === undefined || yRange?.end === undefined) return undefined
		const output: Bar[] = []
		const min = Math.min(0, meta.min)
		const max = Math.max(0, meta.max)
		const xRangeLength = meta.end - meta.beg
		const yRangeLength = max - min
		const zeroPoint = (max / yRangeLength) * svgDim
		const xScale = svgDim / xRangeLength
		let prevBar: Bar | undefined = undefined
		for (let i = 0; i < pointsFormatted.length; i++) {
			const nextPoint = pointsFormatted[i + 1]
			const point = pointsFormatted[i]
			const bar: Bar = { x: 0, ys: [], width: 0 }
			bar.x = (point.x - meta.beg!) * xScale
			bar.ys = point.ys.map((y) => (y / yRangeLength) * svgDim)
			if (interval !== undefined) {
				bar.width = interval * xScale
			} else {
				if (prevBar === undefined) {
					if (!nextPoint) {
						bar.width = Math.min(bar.x, svgDim - bar.x)
					} else {
						bar.width = (nextPoint.x - point.x) * xScale
					}
				} else {
					if (!nextPoint) {
						const dist = bar.x - (prevBar.x + prevBar.width / 2)
						bar.width = dist * 2
					} else {
						bar.width = Math.min((bar.x - prevBar.x + prevBar.width) * 2, (nextPoint.x - point.x) * xScale)
					}
				}
			}
			output.push(bar)
			prevBar = bar
		}
		const forwardPoints: ChartPoint[][] = []
		const backwardPoints: ChartPoint[][] = []
		const allPoints: string[][] = []
		if (barIndex !== undefined && indexesCount !== undefined) {
			for (let bar of output) {
				const relWidth = bar.width * (1 - barSpace)
				const xBeg = bar.x - relWidth / 2 + (barIndex / indexesCount) * relWidth
				const xEnd = xBeg + relWidth / indexesCount
				if (bar.ys.length > forwardPoints.length) {
					forwardPoints.push(...bar.ys.map(() => []))
				}
				if (bar.ys.length > backwardPoints.length) {
					backwardPoints.push(...bar.ys.map(() => []))
				}
				let posPrevPoint = zeroPoint
				let negPrevPoint = zeroPoint
				bar.ys.forEach((y, index) => {
					let prevPoint = y >= 0 ? posPrevPoint : negPrevPoint
					const trueY = prevPoint - y
					const pointF = forwardPoints[index]
					const pointB = backwardPoints[index]
					pointF.push({ x: xBeg, y: prevPoint })
					pointF.push({ x: xBeg, y: trueY })
					pointF.push({ x: xEnd, y: trueY })
					pointF.push({ x: xEnd, y: prevPoint })
					pointB.push({ x: xBeg, y: prevPoint })
					pointB.push({ x: xEnd, y: prevPoint })
					if (y >= 0) {
						posPrevPoint = trueY
					} else {
						negPrevPoint = trueY
					}
				})
			}
		}
		const backwardPointsProcessed = backwardPoints.map((l) => {
			l.reverse()
			let lastY: number | undefined = undefined
			return l.filter((pnt, index) => {
				const lY = lastY
				lastY = pnt.y
				if (lY === undefined) {
					return true
				}
				const nextPoint = l[index + 1]
				if (!nextPoint) return true
				return nextPoint.y !== pnt.y || pnt.y !== lY
			})
		})
		forwardPoints.forEach((l, i) => allPoints.push([...forwardPoints[i].map((i) => `${i.x},${i.y}`), ...backwardPointsProcessed[i].map((i) => `${i.x},${i.y}`)]))
		return allPoints.map((item, index) => ({ points: item, color: color[index] }))
	}, [pointsFormatted, meta, xRange, yRange, color, barSpace, barIndex, indexesCount, interval])

	return (
		<SsvgChartContent height={svgDim} width={svgDim} viewBox={`${viewBox[0]} ${viewBox[1]} ${viewBox[2]} ${viewBox[3]}`} preserveAspectRatio="none">
			{pathStates &&
				pathStates.map((item, index) => {
					const opacity = opacities?.[index] ?? 1
					return (
						<polyline
							key={index}
							points={item.points.join(' ')}
							preserveAspectRatio="none"
							style={{
								fill: item.color,
								fillOpacity: opacity,
								stroke: 'none'
							}}
						/>
					)
				})}
		</SsvgChartContent>
	)
}

export default React.memo(ChartBarSeries)
